import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import CustomDatePicker from "@/components/DateTimePicker";
import api, { formatApiError } from "@/lib/api";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import NumberField from "@/components/NumberField";
import InlineCell from "@/components/InlineCell";
import { fmtDate } from "@/utils/date";
import { Buildings, User as UserIcon, Printer, MoneyWavy } from "@phosphor-icons/react";

const TABS = [
  { key: "company", label: "كشوف الشركات", icon: Buildings },
  { key: "driver", label: "كشوف السائقين", icon: UserIcon },
];

export default function Statements() {
  const [tab, setTab] = useState("company");
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [selected, setSelected] = useState("");
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settleAmt, setSettleAmt] = useState(0);

  const loadLists = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([api.get("/admin/companies"), api.get("/admin/drivers")]);
      setCompanies(c.data);
      setDrivers(d.data);
    } catch {
      toast.error("تعذر التحميل");
    }
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  const fetchStatement = async () => {
    if (!selected) return toast.error("اختر شركة أو سائق");
    setLoading(true);
    setStatement(null);
    try {
      const path = tab === "company" ? `/admin/statements/company/${selected}` : `/admin/statements/driver/${selected}`;
      const params = {};
      if (dfrom) params.dfrom = dfrom;
      if (dto) params.dto = dto;
      const { data } = await api.get(path, { params });
      setStatement(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const settle = async () => {
    if (!settleAmt || Number(settleAmt) <= 0) return toast.error("أدخل مبلغ");
    try {
      await api.post("/admin/settlements", { driver_id: selected, amount: Number(settleAmt), note: `تسوية من ${dfrom || "—"} إلى ${dto || "—"}` });
      toast.success("تمت التسوية");
      setSettleAmt(0);
      fetchStatement();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const printPage = () => window.print();

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-5">كشوف الحسابات</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 print:hidden">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setSelected(""); setStatement(null); }}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  active ? "bg-primary text-primary-foreground" : "border border-primary/25 text-white/70 hover:border-primary/50 hover:text-primary"
                }`}
              >
                <t.icon size={16} weight={active ? "fill" : "duotone"} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5 print:hidden" data-testid="statements-filters">
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">{tab === "company" ? "الشركة" : "السائق"}</div>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} className="mz-input" data-testid="statement-target">
              <option value="">اختر</option>
              {(tab === "company" ? companies : drivers).map((x) => (
                <option key={x.id} value={x.id}>{x.name || x.email}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">من</div>
            <CustomDatePicker value={dfrom} onChange={setDfrom} placeholder="YYYY-MM-DD" testId="statement-from" />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">إلى</div>
            <CustomDatePicker value={dto} onChange={setDto} placeholder="YYYY-MM-DD" testId="statement-to" />
          </div>
          <div className="flex items-end">
            <button onClick={fetchStatement} disabled={loading} className="w-full py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="statement-generate">
              {loading ? "جارٍ التوليد…" : "توليد الكشف"}
            </button>
          </div>
        </div>

        {/* Statement */}
        {statement && (
          <div className="bg-card border border-primary/15 rounded-2xl p-5 fadeup" data-testid="statement-result">
            {/* Print header */}
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-primary/15">
              <div>
                <h2 className="text-primary font-bold text-xl">
                  {tab === "company" ? "كشف حساب شركة" : "كشف حساب سائق"}
                </h2>
                <p className="text-white/50 text-sm mt-1">
                  {statement.driver_name || (companies.find((c) => c.id === selected)?.name) || "—"}
                  {" · "}
                  <span className="num">{fmtDate(dfrom || "—")}</span> — <span className="num">{fmtDate(dto || "—")}</span>
                </p>
              </div>
              <button onClick={printPage} className="print:hidden inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 text-sm" data-testid="statement-print">
                <Printer size={16} weight="duotone" /> طباعة/PDF
              </button>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                <div className="text-white/60 text-xs">عدد الرحلات</div>
                <div className="text-2xl font-bold text-white mt-1 num">{fmtNum(statement.trips || 0)}</div>
              </div>
              {tab === "company" ? (
                <>
                  <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                    <div className="text-white/60 text-xs">الإجمالي</div>
                    <div className="text-2xl font-bold text-primary mt-1 num">{fmtRiyal(statement.total || 0)}</div>
                  </div>
                  <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                    <div className="text-white/60 text-xs">المدفوع</div>
                    <div className="text-2xl font-bold text-[#2E7D32] mt-1 num">{fmtRiyal(statement.paid || 0)}</div>
                  </div>
                  <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                    <div className="text-white/60 text-xs">المتبقي</div>
                    <div className="text-2xl font-bold text-[#F57F17] mt-1 num">{fmtRiyal(statement.outstanding || 0)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                    <div className="text-white/60 text-xs">إجمالي الأجور</div>
                    <div className="text-2xl font-bold text-primary mt-1 num">{fmtRiyal(statement.total_wages || 0)}</div>
                  </div>
                  <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                    <div className="text-white/60 text-xs">المدفوع للسائق</div>
                    <div className="text-2xl font-bold text-[#2E7D32] mt-1 num">{fmtRiyal(statement.paid_out || 0)}</div>
                  </div>
                  <div className="rounded-2xl p-4 border border-primary/20 bg-[#0B1A3A] text-center">
                    <div className="text-white/60 text-xs">المستحق</div>
                    <div className="text-2xl font-bold text-[#F57F17] mt-1 num">{fmtRiyal(statement.outstanding || 0)}</div>
                  </div>
                </>
              )}
            </div>

            {/* Driver settlement */}
            {tab === "driver" && (
              <div className="print:hidden mb-5 p-4 rounded-2xl border border-primary/25 bg-[#0B1A3A]/60 flex items-end gap-3 flex-wrap">
                <MoneyWavy size={22} weight="duotone" className="text-primary" />
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs text-white/60 mb-1 text-right">مبلغ التسوية (ر.س)</div>
                  <NumberField min={0} value={settleAmt} onChange={setSettleAmt} className="mz-input" testId="settle-amount" placeholder="0" />
                </div>
                <button onClick={settle} className="px-5 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold" data-testid="settle-apply">تسجيل تسوية</button>
              </div>
            )}

            {/* Bookings table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]" data-testid="statement-table">
                <thead className="bg-[#0B1A3A] text-primary/80 text-xs">
                  <tr>
                    <th className="p-3 text-start">#</th>
                    <th className="p-3 text-start">رقم الحجز</th>
                    <th className="p-3 text-start">التاريخ</th>
                    <th className="p-3 text-start" style={{ minWidth: 340 }}>المسارات{tab === "company" && " (المدفوع لكل مسار)"}</th>
                    {tab === "driver" && <th className="p-3 text-start">عميل كاش</th>}
                    <th className="p-3 text-start">{tab === "company" ? "السعر" : "صافي أجرة السائق"}</th>
                    {tab === "company" && <th className="p-3 text-start">إجمالي المدفوع</th>}
                    {tab === "company" && <th className="p-3 text-start">المتبقي</th>}
                    <th className="p-3 text-start">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {(statement.bookings || []).map((b, i) => {
                    const cash = Number(b.cash_from_client || 0);
                    const wage = Number(b.driver_wage || 0);
                    const netWage = Math.max(0, wage - cash);
                    const totalCell = Number(b.price || b.total_price || 0);
                    // 2026-02-28 §PerRoutePaid — paid is now stored per route
                    // (`routes[N].paid_amount`); booking-level `paid_amount`
                    // is a materialised sum kept in sync by the backend.
                    // If a booking has NO routes[], fall back to the
                    // booking-level value so legacy single-leg rows still
                    // render correctly.
                    const bookingPaid = Number(b.paid_amount || 0);
                    const remainingCell = Math.max(0, totalCell - bookingPaid);
                    const priceCell = tab === "company" ? totalCell : netWage;
                    const routes = Array.isArray(b.routes) && b.routes.length
                      ? b.routes
                      : [{ pickup_location: b.pickup_location, destination: b.destination || b.dropoff_location, trip_date: b.trip_date, cars: null, paid_amount: bookingPaid, route_total: totalCell }];
                    return (
                    <tr key={b.id} className="border-t border-primary/10 align-top">
                      <td className="p-3 num">{fmtNum(i + 1)}</td>
                      <td className="p-3 num text-primary">{b.booking_number}</td>
                      <td className="p-3 num">{fmtDate(b.pickup_time || b.trip_date)}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-2">
                          {routes.map((r, ri) => {
                            const carsList = Array.isArray(r.cars) && r.cars.length
                              ? r.cars
                                  .map((c) => c.car_name_ar || c.car_type)
                                  .filter(Boolean)
                                  .join(" · ")
                              : (b.car_type_name || b.car_type || "");
                            const routeTotal = Number(r.route_total || 0);
                            const routePaid = Number(r.paid_amount || 0);
                            return (
                              <div
                                key={ri}
                                className="text-xs leading-relaxed flex flex-wrap items-center gap-x-2 gap-y-1 border-r-2 border-primary/20 pr-2"
                                data-testid={`stmt-route-${b.id}-${ri}`}
                              >
                                <span className="text-primary/60">#{fmtNum(ri + 1)}</span>
                                <span className="text-white">{r.pickup_location || "—"}</span>
                                <span className="text-primary/70">→</span>
                                <span className="text-white">{r.destination || "—"}</span>
                                {carsList && (
                                  <span className="text-white/60">· <span className="text-white/80">{carsList}</span></span>
                                )}
                                {r.trip_date && (
                                  <span className="text-white/40 num" dir="ltr">· {fmtDate(r.trip_date)}</span>
                                )}
                                {routeTotal > 0 && (
                                  <span className="text-primary/80 num" title="سعر المسار">· {fmtNum(routeTotal)} ر.س</span>
                                )}
                                {/* 2026-02-28 §PerRoutePaid inline editor.
                                    Only shows on the company statement tab
                                    and only if we have a real booking id
                                    (b.id) and route position (ri). Backend
                                    keeps booking-level paid_amount = Σ
                                    routes[*].paid_amount automatically. */}
                                {tab === "company" && (
                                  <>
                                    <span
                                      className="inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded-md bg-[#2E7D32]/12 border border-[#2E7D32]/40"
                                      title="المدفوع من هذا المسار"
                                    >
                                      <MoneyWavy size={11} weight="fill" className="text-[#2E7D32]" />
                                      <span className="text-[10px] text-[#2E7D32]/70">مدفوع</span>
                                      <InlineCell
                                        bookingId={b.id}
                                        value={routePaid}
                                        type="number"
                                        endpoint={`/admin/bookings/${b.id}/route-financials`}
                                        method="patch"
                                        buildPayload={(v) => ({ route_idx: ri, paid_amount: Number(v) || 0 })}
                                        onSaved={(u) => {
                                          const upd = u?.booking || u;
                                          if (!upd?.id) return;
                                          setStatement((s) => {
                                            if (!s) return s;
                                            const newBookings = (s.bookings || []).map((row) => {
                                              if (row.id !== upd.id) return row;
                                              return { ...row, routes: upd.routes || row.routes, paid_amount: upd.paid_amount };
                                            });
                                            const newPaid = newBookings.reduce((a, x) => a + Number(x.paid_amount || 0), 0);
                                            return { ...s, bookings: newBookings, paid: newPaid, outstanding: Math.max(0, (s.total || 0) - newPaid) };
                                          });
                                        }}
                                        testId={`inline-route-paid-${b.id}-${ri}`}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        display={(v) => Number(v) > 0
                                          ? <span className="num text-[#2E7D32] font-bold">{fmtNum(v)}</span>
                                          : <span className="text-white/40 num">0</span>}
                                      />
                                    </span>
                                    {/* 2026-02-28 §RemainingInverseStmt — the
                                        admin can type the still-owed amount
                                        directly; backend infers paid_amount
                                        from `remaining_amount` = total-paid. */}
                                    {routeTotal > 0 && (
                                      <span
                                        className="inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded-md bg-[#F57F17]/12 border border-[#F57F17]/40"
                                        title="المتبقي من سعر هذا المسار"
                                      >
                                        <span className="text-[10px] text-[#F57F17]/80">متبقي</span>
                                        <InlineCell
                                          bookingId={b.id}
                                          value={Math.max(0, routeTotal - routePaid)}
                                          type="number"
                                          endpoint={`/admin/bookings/${b.id}/route-financials`}
                                          method="patch"
                                          buildPayload={(v) => ({ route_idx: ri, remaining_amount: Number(v) || 0 })}
                                          onSaved={(u) => {
                                            const upd = u?.booking || u;
                                            if (!upd?.id) return;
                                            setStatement((s) => {
                                              if (!s) return s;
                                              const newBookings = (s.bookings || []).map((row) => {
                                                if (row.id !== upd.id) return row;
                                                return { ...row, routes: upd.routes || row.routes, paid_amount: upd.paid_amount };
                                              });
                                              const newPaid = newBookings.reduce((a, x) => a + Number(x.paid_amount || 0), 0);
                                              return { ...s, bookings: newBookings, paid: newPaid, outstanding: Math.max(0, (s.total || 0) - newPaid) };
                                            });
                                          }}
                                          testId={`inline-route-remaining-${b.id}-${ri}`}
                                          inputProps={{ min: 0, step: 0.01 }}
                                          display={(v) => Number(v) > 0
                                            ? <span className="num text-[#F57F17] font-bold">{fmtNum(v)}</span>
                                            : <span className="text-white/40 num">0</span>}
                                        />
                                      </span>
                                    )}
                                    {/* Payment method chip — synced two-way
                                        with /company-bookings inline dropdown. */}
                                    <InlineCell
                                      bookingId={b.id}
                                      value={r.payment_method || ""}
                                      type="select"
                                      options={[
                                        { value: "", label: "طريقة الدفع" },
                                        { value: "تحويل", label: "تحويل" },
                                        { value: "كاش", label: "كاش" },
                                        { value: "مختلط", label: "مختلط" },
                                      ]}
                                      endpoint={`/admin/bookings/${b.id}/route-financials`}
                                      method="patch"
                                      buildPayload={(v) => ({ route_idx: ri, payment_method: String(v || "") })}
                                      onSaved={(u) => {
                                        const upd = u?.booking || u;
                                        if (!upd?.id) return;
                                        setStatement((s) => {
                                          if (!s) return s;
                                          const newBookings = (s.bookings || []).map((row) => {
                                            if (row.id !== upd.id) return row;
                                            return { ...row, routes: upd.routes || row.routes };
                                          });
                                          return { ...s, bookings: newBookings };
                                        });
                                      }}
                                      testId={`inline-route-pm-${b.id}-${ri}`}
                                      display={(v) => {
                                        const s = String(v || "").trim();
                                        if (!s) return <span className="text-white/40 text-[10px]">طريقة الدفع</span>;
                                        const styles = {
                                          "تحويل": "#3B82F6",
                                          "كاش":   "#22C55E",
                                          "مختلط": "#8B5CF6",
                                        };
                                        const c = styles[s] || "#94A3B8";
                                        return (
                                          <span
                                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                                            style={{ background: `${c}22`, border: `1px solid ${c}66`, color: c }}
                                          >
                                            {s}
                                          </span>
                                        );
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      {tab === "driver" && (
                        <td
                          className="p-3 num font-bold"
                          style={{ color: cash > 0 ? "#3B82F6" : "rgba(255,255,255,0.35)" }}
                          title={cash > 0 ? "استلمها السائق نقداً من العميل" : ""}
                        >
                          {cash > 0 ? fmtRiyal(cash) : "—"}
                        </td>
                      )}
                      <td className="p-3 num text-primary font-bold" title={tab === "driver" && cash > 0 ? `أجرة قبل الخصم: ${fmtRiyal(wage)}` : undefined}>
                        {fmtRiyal(priceCell)}
                      </td>
                      {tab === "company" && (
                        <td className="p-3 num font-bold text-[#2E7D32]" data-testid={`stmt-total-paid-${b.id}`}>
                          {bookingPaid > 0 ? fmtRiyal(bookingPaid) : <span className="text-white/35">—</span>}
                        </td>
                      )}
                      {tab === "company" && (
                        <td className="p-3 num font-bold text-[#F57F17]" data-testid={`stmt-remaining-${b.id}`}>
                          {remainingCell > 0 ? fmtRiyal(remainingCell) : <span className="text-white/35">—</span>}
                        </td>
                      )}
                      <td className="p-3 text-white/60 text-xs">{b.status}</td>
                    </tr>
                  );})}
                  {(!statement.bookings || statement.bookings.length === 0) && (
                    <tr><td colSpan={tab === "driver" ? 7 : 8} className="p-8 text-center text-white/40">لا توجد رحلات في هذه الفترة</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!statement && !loading && (
          <div className="text-center py-16 text-white/40 bg-card border border-primary/15 rounded-2xl">
            اختر {tab === "company" ? "شركة" : "سائق"} وحدد الفترة لتوليد الكشف
          </div>
        )}
      </main>
    </div>
  );
}
